import type {
  BuilderRecipeDetailsFragment,
  RecipeDetailsFragment
} from '../../__generated__/apollo-hooks';
import { transformRecipeToNodesAndLinks } from '../../components/recipe-topology-viewer/recipe-topology.helper';
import { ROOT_NODE_ID, useBuilderStore } from '../../stores/builder.store';
import type {
  BuilderRecipe,
  GraphLink,
  GraphNodeData
} from '../indexedDb.helper';

import {
  cleanLinks,
  findDescendantNodeIds,
  findParentNodeIDs
} from './builder-links.helpers';
import {
  makeRecipeIdCustom,
  makeRecipeNameCustom
} from './builder-naming.helpers';
import { findNextAvailableId } from './builder-nodes.helpers';

/**
 * The marketplace recipe id for a Builder recipe is its root node's `val.id` — the value
 * written as the YAML `name` and used by the server as the RecipeDescriptor id after install.
 */
export const getBuilderRecipeMarketplaceId = (
  recipe: BuilderRecipe | null | undefined
): string | undefined =>
  recipe?.nodes?.find((node) => node.id === ROOT_NODE_ID)?.val?.id;

/**
 * In the case where we are creating a whole new recipe node (not moving an
 * existing node) we need to offset the new node IDs by 1 to avoid collisions.
 */
const OFFSET = 1;

/**
 * Removes fields from a persisted BuilderRecipe's node vals that the builder
 * fragment no longer fetches: `tags` from every node, and `description` from
 * every non-root node. Applied when loading recipes from IndexedDB (both V1
 * import and V2 reads) so in-memory state matches the current fragment shape.
 *
 * Why strip at all: old fragment shapes populated `val.description` on every
 * nested node and YAML-imported paths populated `val.tags`. Neither is read
 * on the builder path anymore — leaving them in memory causes structural
 * type drift when passing the recipe to `addRecipeToNode` and friends.
 */
export const stripLegacyBuilderFields = (
  recipe: BuilderRecipe | undefined
): BuilderRecipe | undefined => {
  if (!recipe?.nodes) {
    return recipe;
  }
  return {
    ...recipe,
    nodes: recipe.nodes.map((node) => {
      if (!node.val) {
        return node;
      }
      const cleaned: Record<string, unknown> = { ...node.val };
      delete cleaned.tags;
      delete cleaned.recipeList;
      delete cleaned.preconditions;
      delete cleaned.graph;
      if (node.id !== ROOT_NODE_ID) {
        delete cleaned.description;
      }
      return {
        ...node,
        val: cleaned as GraphNodeData['val']
      };
    })
  };
};

/**
 * Create new intermediate node that will hold the leaf node and a new recipe
 * including the children of the new recipe.
 */
export const convertLeafNodeToIntermediateRecipe = (
  originalLeafNodeId: number,
  newRecipeId: number,
  nodesToAttach: GraphNodeData[],
  linksToAttach: GraphLink[]
) => {
  const currentRecipe = useBuilderStore.getState().currentRecipe;
  const originalLeafNode = currentRecipe.nodes.find(
    (node) => node.id === originalLeafNodeId
  );
  const intermediateNodeId =
    findNextAvailableId(currentRecipe.nodes) + linksToAttach.length + OFFSET;

  const intermediateNode = {
    id: intermediateNodeId,
    name: makeRecipeNameCustom(originalLeafNode?.name),
    isCustomRecipe: true,
    isPrecondition: originalLeafNode?.isPrecondition,
    val: {
      name: makeRecipeNameCustom(originalLeafNode?.name),
      id: makeRecipeIdCustom(originalLeafNode?.val?.id, intermediateNodeId)
    }
  };

  // Connect intermediate node to original leaf node and new recipe node
  const newLinks = [
    { source: intermediateNodeId, target: originalLeafNode?.id },
    { source: intermediateNodeId, target: newRecipeId },
    ...linksToAttach
  ];

  return {
    intermediateNodeId,
    newNodes: [...nodesToAttach, intermediateNode],
    newLinks: newLinks
  };
};

export const addRecipeDetailsFragmentToNode = (
  currentRecipe: BuilderRecipe,
  recipe: RecipeDetailsFragment | BuilderRecipeDetailsFragment,
  nodeId: number,
  isPrecondition?: boolean,
  /**
   * When adding to a recipe with children add to the top or the bottom
   */
  addToTop?: boolean,
  isMissingRecipe?: boolean
) => {
  const descendantNodeIds = findDescendantNodeIds(nodeId, currentRecipe?.links);
  const nextAvailableId = findNextAvailableId(currentRecipe?.nodes);

  const { nodes, links } = transformRecipeToNodesAndLinks(
    recipe,
    nextAvailableId,
    isPrecondition
  );

  if (isMissingRecipe && nodes.length > 0) {
    nodes.at(0).isMissingRecipe = true;
  }

  const hasChildren = descendantNodeIds.length > 0;

  // biome-ignore lint/suspicious/noEvolvingTypes: BuilderRecipe is too strict — intermediate nodes have partial val
  let newRecipeState;

  if (hasChildren || nodeId === ROOT_NODE_ID) {
    if (addToTop) {
      newRecipeState = {
        ...currentRecipe,
        nodes: [...nodes, ...currentRecipe.nodes],
        links: [
          { source: nodeId, target: nextAvailableId },
          ...links,
          ...currentRecipe.links
        ]
      };
    } else {
      newRecipeState = {
        ...currentRecipe,
        nodes: [...currentRecipe.nodes, ...nodes],
        links: [
          ...currentRecipe.links,
          ...links,
          { source: nodeId, target: nextAvailableId }
        ]
      };
    }
  } else {
    const { newNodes, newLinks, intermediateNodeId } =
      convertLeafNodeToIntermediateRecipe(
        nodeId,
        nextAvailableId,
        nodes,
        links
      );

    // update the newLinks with existing links
    currentRecipe.links.forEach((link) => {
      if (link.target !== nodeId) {
        newLinks.push(link);
      } else {
        newLinks.push({ source: link.source, target: intermediateNodeId });
      }
    });

    newRecipeState = {
      ...currentRecipe,
      nodes: [...currentRecipe.nodes, ...newNodes],
      links: cleanLinks(newLinks)
    };
  }

  return {
    newRecipeState,
    nextAvailableId
  };
};

export const makeParentsCustomRecipe = (
  id: number,
  currentRecipe,
  includeSelf = false
): BuilderRecipe => {
  const allParents = findParentNodeIDs(id, currentRecipe.links);
  const newNodes = currentRecipe.nodes.map((n) => {
    if (allParents.includes(n.id) || (includeSelf && n.id === id)) {
      return {
        id: n.id,
        name: n.isCustomRecipe ? n.name : makeRecipeNameCustom(n.name),
        isCustomRecipe: true,
        isPrecondition: n.isPrecondition ?? false,
        val: {
          ...n?.val,
          name: n.isCustomRecipe ? n.name : makeRecipeNameCustom(n.name),
          id: n.isCustomRecipe
            ? n?.val?.id
            : makeRecipeIdCustom(n?.val?.id, n.id)
        }
      };
    }
    return n;
  });
  const newRecipe = {
    ...currentRecipe,
    nodes: newNodes
  };
  return newRecipe;
};

export const updateSelectedNodeOptions = async (
  options: RecipeDetailsFragment['options']
) => {
  const { selectedRecipeNode, descendantNodeIds } = useBuilderStore.getState();
  const selectedRecipeNodeId = selectedRecipeNode.id;

  const currentRecipe = makeParentsCustomRecipe(
    selectedRecipeNodeId,
    useBuilderStore.getState().currentRecipe
  );

  const hasChildren = descendantNodeIds.size > 0;
  const newNodes = currentRecipe.nodes.map((n) => {
    if (n.id === selectedRecipeNodeId) {
      if (hasChildren) {
        return {
          id: n.id,
          name: n.name,
          isCustomRecipe: true,
          isPrecondition: n.isPrecondition,
          val: {
            ...n?.val,
            id: makeRecipeIdCustom(n?.val?.id, n.id),
            name: makeRecipeNameCustom(n?.val?.name),
            options
          }
        };
      }
      // Is a leaf so it is a standard recipe and does not need a custom name
      return {
        id: n.id,
        name: n.name,
        isCustomRecipe: false,
        isPrecondition: n.isPrecondition,
        val: {
          ...n?.val,
          options
        }
      };
    }
    return n;
  });

  await useBuilderStore.getState().saveCustomRecipe({
    recipe: {
      id: currentRecipe.id,
      name: currentRecipe.name,
      nodes: newNodes,
      links: currentRecipe.links
    }
  });
  // reselect the recipe node so user doesn't lose context
  await useBuilderStore.getState().updateSelectedRecipe(selectedRecipeNodeId);
};

export const updateCurrentRecipeDetails = async (
  name,
  id,
  description,
  nodeId = ROOT_NODE_ID
) => {
  const isNestedRecipe = nodeId !== ROOT_NODE_ID;
  const currentRecipe = useBuilderStore.getState().currentRecipe;
  if (!currentRecipe) {
    return;
  }
  const newRecipe = {
    // keep original id/name for root if we are modifying a nested recipe
    id: isNestedRecipe ? currentRecipe.id : id,
    name: isNestedRecipe ? currentRecipe.name : name,
    nodes: currentRecipe?.nodes.map((n) => {
      if (n.id === nodeId) {
        return {
          id: n.id,
          name,
          isCustomRecipe: true,
          isPrecondition: n.isPrecondition,
          val: {
            ...n?.val,
            name,
            id,
            description
          }
        };
      }
      return n;
    }),
    links: currentRecipe?.links
  };

  await useBuilderStore.getState().saveCustomRecipe({
    recipe: newRecipe,
    forceSave: isNestedRecipe || (id === currentRecipe.id && !isNestedRecipe)
  });
};

export const reloadCurrentRecipe = async () => {
  const { currentRecipe, loadCustomRecipe } = useBuilderStore.getState();
  await loadCustomRecipe(currentRecipe?.id);
};
